// Imports
import React, { useMemo } from 'react';
import { ThemeProvider } from 'styled-components/macro';
import FreshStyle from './fresh-style';
import ModernStyle from './modern-style';
import NatureStyle from './nature-style';
import PaperStyle from './paper-style';


// Define what a template variant looks like
interface TemplateVariant {
	style: 'fresh' | 'modern' | 'nature' | 'paper';
	baseColor: string;
	contentLinkColor: string;
	bottomInfoBackgroundColor: string;
	bottomInfoLinkColor: string;
	calendarEventColor?: string;
	customResellerPhraseLighten?: number;
	isFlipped?: true;
	isAutumn?: true;
	isPebbles?: true;
}


// Fresh templates
const freshBlue: TemplateVariant = {
	style: 'fresh',
	baseColor: 'hsl(208, 73%, 40%)',
	contentLinkColor: 'hsl(208, 73%, 45%)',
	bottomInfoBackgroundColor: 'hsl(208, 74%, 95%)',
	bottomInfoLinkColor: 'hsl(208, 73%, 42%)',
};

const freshGreen: TemplateVariant = {
	style: 'fresh',
	baseColor: 'hsl(87, 68%, 41%)',
	contentLinkColor: 'hsl(87, 68%, 30%)',
	bottomInfoBackgroundColor: 'hsl(87, 69%, 96%)',
	bottomInfoLinkColor: 'hsl(87, 68%, 29%)',
	customResellerPhraseLighten: 0.05,
};

const freshBrown: TemplateVariant = {
	style: 'fresh',
	baseColor: 'hsl(35, 50%, 42%)',
	contentLinkColor: 'hsl(35, 50%, 39%)',
	bottomInfoBackgroundColor: 'hsl(35, 52%, 97%)',
	bottomInfoLinkColor: 'hsl(35, 50%, 38%)',
};

const freshRed: TemplateVariant = {
	style: 'fresh',
	baseColor: 'hsl(0, 55%, 41%)',
	contentLinkColor: 'hsl(0, 55%, 51%)',
	bottomInfoBackgroundColor: 'hsl(0, 56%, 96%)',
	bottomInfoLinkColor: 'hsl(0, 55%, 50%)',
};

const freshTeal: TemplateVariant = {
	style: 'fresh',
	baseColor: 'hsl(164, 54%, 42%)',
	contentLinkColor: 'hsl(164, 54%, 33%)',
	bottomInfoBackgroundColor: 'hsl(165, 54%, 97%)',
	bottomInfoLinkColor: 'hsl(164, 54%, 32%)',
	customResellerPhraseLighten: 0.05,
};

const freshPurple: TemplateVariant = {
	style: 'fresh',
	baseColor: 'hsl(254, 45%, 51%)',
	contentLinkColor: 'hsl(254, 45%, 59%)',
	bottomInfoBackgroundColor: 'hsl(258, 45%, 97%)',
	bottomInfoLinkColor: 'hsl(254, 45%, 57%)',
};


// Modern templates
const modernBlue: TemplateVariant = {
	style: 'modern',
	baseColor: 'hsl(208, 73%, 40%)',
	contentLinkColor: 'hsl(208, 73%, 45%)',
	bottomInfoBackgroundColor: 'hsl(208, 74%, 85%)',
	bottomInfoLinkColor: 'hsl(208, 73%, 36%)',
};

const modernDarkBlue: TemplateVariant = {
	style: 'modern',
	baseColor: 'hsl(212, 45%, 37%)',
	contentLinkColor: 'hsl(212, 45%, 47%)',
	bottomInfoBackgroundColor: 'hsl(212, 45%, 82%)',
	bottomInfoLinkColor: 'hsl(212, 45%, 36%)',
};

const modernGreen: TemplateVariant = {
	style: 'modern',
	baseColor: 'hsl(87, 68%, 41%)',
	contentLinkColor: 'hsl(87, 68%, 30%)',
	bottomInfoBackgroundColor: 'hsl(87, 69%, 96%)',
	bottomInfoLinkColor: 'hsl(87, 68%, 29%)',
};

const modernDarkGreen: TemplateVariant = {
	style: 'modern',
	baseColor: 'hsl(87, 43%, 29%)',
	contentLinkColor: 'hsl(87, 42%, 35%)',
	bottomInfoBackgroundColor: 'hsl(88, 43%, 85%)',
	bottomInfoLinkColor: 'hsl(87, 43%, 29%)',
};

const modernBrown: TemplateVariant = {
	style: 'modern',
	baseColor: 'hsl(28, 50%, 34%)',
	contentLinkColor: 'hsl(28, 50%, 42%)',
	bottomInfoBackgroundColor: 'hsl(28, 50%, 79%)',
	bottomInfoLinkColor: 'hsl(28, 50%, 31%)',
};

const modernRed: TemplateVariant = {
	style: 'modern',
	baseColor: 'hsl(0, 52%, 38%)',
	contentLinkColor: 'hsl(0, 52%, 48%)',
	bottomInfoBackgroundColor: 'hsl(0, 52%, 83%)',
	bottomInfoLinkColor: 'hsl(0, 52%, 38%)',
};

const modernOrange: TemplateVariant = {
	style: 'modern',
	baseColor: 'hsl(38, 98%, 38%)',
	contentLinkColor: 'hsl(38, 98%, 32%)',
	bottomInfoBackgroundColor: 'hsl(38, 98%, 93%)',
	bottomInfoLinkColor: 'hsl(38, 98%, 30%)',
};


// Nature templates
const natureAutumn: TemplateVariant = {
	style: 'nature',
	baseColor: 'hsl(0, 52%, 38%)',
	contentLinkColor: 'hsl(0, 52%, 48%)',
	bottomInfoBackgroundColor: 'hsl(0, 52%, 83%)',
	bottomInfoLinkColor: 'hsl(0, 52%, 38%)',
	isAutumn: true,
};

const naturePebbles: TemplateVariant = {
	style: 'nature',
	baseColor: 'hsl(0, 0%, 53%)',
	contentLinkColor: 'hsl(0, 0%, 0%)',
	bottomInfoBackgroundColor: 'hsl(0, 0%, 97%)',
	bottomInfoLinkColor: 'hsl(0, 0%, 0%)',
	calendarEventColor: 'hsl(0, 0%, 45%)',
	isPebbles: true,
};


// Paper templates
const paperWhite: TemplateVariant = {
	style: 'paper',
	baseColor: 'hsl(0, 0%, 0%)',
	contentLinkColor: 'hsl(0, 0%, 10%)',
	bottomInfoBackgroundColor: 'hsl(0, 0%, 100%)',
	bottomInfoLinkColor: 'hsl(0, 0%, 10%)',
	customResellerPhraseLighten: 0.4,
};


// Define the variant for each template ID
export const templateVariants: Partial<Record<number, TemplateVariant>> = {
	// Fresh templates
	1: freshBlue,
	2: { ...freshBlue, isFlipped: true },
	3: freshGreen,
	4: { ...freshGreen, isFlipped: true },
	5: freshBrown,
	6: { ...freshBrown, isFlipped: true },
	7: freshRed,
	8: { ...freshRed, isFlipped: true },
	9: freshTeal,
	10: { ...freshTeal, isFlipped: true },
	11: freshPurple,
	12: { ...freshPurple, isFlipped: true },
	
	
	// Modern templates
	13: modernBlue,
	14: { ...modernBlue, isFlipped: true },
	15: modernDarkBlue,
	16: { ...modernDarkBlue, isFlipped: true },
	17: modernGreen,
	18: { ...modernGreen, isFlipped: true },
	19: modernDarkGreen,
	20: { ...modernDarkGreen, isFlipped: true },
	21: modernBrown,
	22: { ...modernBrown, isFlipped: true },
	23: modernRed,
	24: { ...modernRed, isFlipped: true },
	25: modernOrange,
	26: { ...modernOrange, isFlipped: true },
	
	
	// Nature templates
	27: natureAutumn,
	28: { ...natureAutumn, isFlipped: true },
	29: naturePebbles,
	30: { ...naturePebbles, isFlipped: true },
	
	
	// Paper templates
	31: paperWhite,
	32: { ...paperWhite, isFlipped: true },
};


// Returns the theme object for a template variant
//  -> Without a variant, this gives the Fresh blue colors without any template style, so components render without page-level styling like the background gradient
const buildThemeProps = (templateVariant?: TemplateVariant) => {
	// Initialize
	const colorVariant = templateVariant ?? freshBlue;
	
	
	// Return
	return {
		baseColor: colorVariant.baseColor,
		fontFamily: "'Trebuchet MS', 'Arial', 'Helvetica', 'sans-serif'",
		contentLinkColor: colorVariant.contentLinkColor,
		bottomInfoBackgroundColor: colorVariant.bottomInfoBackgroundColor,
		bottomInfoLinkColor: colorVariant.bottomInfoLinkColor,
		calendarEventColor: colorVariant.calendarEventColor ?? colorVariant.contentLinkColor,
		customResellerPhraseLighten: templateVariant?.customResellerPhraseLighten,
		isFlipped: templateVariant?.isFlipped ?? false,
		isFreshStyle: templateVariant?.style === 'fresh',
		isNatureStyle: templateVariant?.style === 'nature',
		isModernOrNatureStyle: templateVariant?.style === 'modern' || templateVariant?.style === 'nature',
		isPaper: templateVariant?.style === 'paper',
		isAutumn: templateVariant?.isAutumn ?? false,
		isPebbles: templateVariant?.isPebbles ?? false,
	};
};


// Returns the theme object for a template ID
export const generateThemeProps = (templateID: number) => buildThemeProps(templateVariants[templateID]);


// Define the theme for components rendered outside of any template (e.g., in component stories)
export const defaultThemeProps = buildThemeProps();


// Export the type for re-use
export type GeneratedThemeProps = ReturnType<typeof buildThemeProps>;


// Define the global style for each template style
const globalStyles: Record<TemplateVariant['style'], typeof FreshStyle> = {
	fresh: FreshStyle,
	modern: ModernStyle,
	nature: NatureStyle,
	paper: PaperStyle,
};


// Define the accepted props
interface Props extends React.PropsWithChildren {
	id: number;
}


// Function component
const TemplateStyle: React.FC<Props> = ({ id, children }) => {
	// Generate the theme only when the template changes
	const theme = useMemo(() => generateThemeProps(id), [id]);
	
	
	// Determine which global style to apply, if any
	const templateStyle = templateVariants[id]?.style;
	const GlobalStyle = templateStyle ? globalStyles[templateStyle] : null;
	
	
	// Return JSX
	return (
		<ThemeProvider theme={theme}>
			{GlobalStyle && <GlobalStyle />}
			{children}
		</ThemeProvider>
	);
};

export default TemplateStyle;
